import { useEffect, useRef } from "react";
import { sleep } from "src/utils/common";
import { getDescribeService as getDescribeServiceReq } from "src/service/cloud";
import { getMyServiceList } from "src/service/console";
import { getProductFromSessionStorage } from "src/utils/storage/auth";
import { useIsFiresideProduct } from "src/hooks/recoil/useFiresideProduct";

interface UseProvisioningPollParams {
  serviceUid: string;
  onProgress: (status: string, percent: number) => void;
  onComplete: () => void;
}

export function useProvisioningPoll({
  serviceUid,
  onProgress,
  onComplete,
}: UseProvisioningPollParams) {
  const abortRef = useRef<{ aborted: boolean } | null>(null);
  const { isFiresideProduct } = useIsFiresideProduct();
  useEffect(() => {
    if (!serviceUid) return;
    const token = { aborted: false };
    abortRef.current = token;

    const run = async () => {
      let cloudServiceInfo = await getDescribeServiceReq({ serviceUid });
      let beforeStatus = "";
      let count = 1;
      let createPercent = 0;
      const stableStatuses = ["SERVICE", "NEED_SERVICE_RESTART"];

      while (!stableStatuses.includes(cloudServiceInfo.serviceStatus)) {
        if (abortRef.current !== token || token.aborted) break;
        if (!getProductFromSessionStorage() || isFiresideProduct) break;

        const serviceNotice = cloudServiceInfo.serviceStatus;
        switch (serviceNotice) {
          case "REGIST":
            if (beforeStatus !== serviceNotice) {
              count = 1;
              beforeStatus = serviceNotice;
            } else if (count <= 4) {
              count++;
            }
            createPercent = 0.5 * count;
            break;
          case "INITIALIZING":
            if (beforeStatus !== serviceNotice) {
              count = 1;
              beforeStatus = serviceNotice;
            } else if (count <= 48) {
              count++;
            }
            createPercent = 2 + 0.5 * count;
            break;
          case "DB_SETTING":
            if (beforeStatus !== serviceNotice) {
              count = 1;
              beforeStatus = serviceNotice;
            } else if (count <= 4) {
              count++;
            }
            createPercent = 26 + 0.5 * count;
            break;
          case "SETTING_CONFIG":
            if (beforeStatus !== serviceNotice) {
              count = 1;
              beforeStatus = serviceNotice;
            } else if (count <= 2) {
              count++;
            }
            createPercent = 28 + 0.5 * count;
            break;
          case "CREATE_CERTIFICATION":
            if (beforeStatus !== serviceNotice) {
              count = 1;
              beforeStatus = serviceNotice;
            } else if (count <= 2) {
              count++;
            }
            createPercent = 29 + 0.5 * count;
            break;
          case "SERVICE_BOOTING":
            if (beforeStatus !== serviceNotice) {
              count = 1;
              beforeStatus = serviceNotice;
            } else if (count <= 136) {
              count++;
            }
            createPercent = 30 + 0.5 * count;
            break;
          case "READY_FOR_DEPLOY_URL":
            if (beforeStatus !== serviceNotice) {
              count = 1;
              beforeStatus = serviceNotice;
            } else if (count <= 4) {
              count++;
            }
            createPercent = 98 + 0.5 * count;
            break;
        }
        onProgress(serviceNotice, createPercent);
        await sleep(5000);
        if (abortRef.current !== token || token.aborted) break;
        cloudServiceInfo = await getDescribeServiceReq({ serviceUid });
        const list = await getMyServiceList({
          product: getProductFromSessionStorage(),
        });
        if (list?.length === 0) return;
      }

      if (abortRef.current === token && !token.aborted) {
        onProgress("", 0);
        onComplete();
      }
    };

    run();
    return () => {
      token.aborted = true;
    };
  }, [serviceUid, onProgress, onComplete]);
}
